import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useCategories } from "../../lib/categoriesContext";
import { LANG_LABEL, TEMPLATE_LABELS } from "../../lib/categories";
import { Logo } from "../../components/Logo";
import { ArrowLeft, Save, Eye, Upload, X, Plus, Image as ImageIcon, Video, Music, Quote } from "lucide-react";

const TEMPLATES = [
  { v: "long_form", l: "Long-format", d: "Article éditorial complet" },
  { v: "news", l: "Actualité", d: "Brève + image + source" },
  { v: "educational", l: "Pédagogique", d: "Body + bloc À retenir + sources" },
  { v: "data", l: "Donnée", d: "Stat phare + analyse" },
  { v: "myth_buster", l: "Mythe / Réalité", d: "MYTHE vs RÉALITÉ" },
  { v: "case_study", l: "Étude de cas", d: "Pays + récit structuré" },
  { v: "gallery", l: "Galerie photo", d: "Plusieurs images en grille" },
  { v: "qa", l: "Interview Q&A", d: "Questions / Réponses alternées" },
  { v: "video", l: "Vidéo", d: "Embed YouTube/Vimeo" },
  { v: "podcast", l: "Podcast / Audio", d: "SoundCloud, Spotify, audio direct" },
  { v: "infographic", l: "Infographie", d: "Image XL + légende détaillée" },
];

const EMPTY = {
  title: "", subtitle: "", body: "",
  category_slug: "", language: "fr", tags: [], featured_image: "",
  template: "long_form", sources: [], key_facts: [], status: "draft", featured: false,
  myth: "", reality: "", stat_value: "", stat_label: "", country: "",
  gallery_images: [], qa_pairs: [],
  video_url: "", video_caption: "",
  audio_url: "", audio_caption: "", embed_html: "",
  media_blocks: [],
};

export default function ArticleEditor() {
  const { id } = useParams();
  const isNew = !id || id === "nouveau";
  const { user } = useAuth();
  const { categories } = useCategories();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [tagInput, setTagInput] = useState("");
  const [factInput, setFactInput] = useState("");
  const [sourceLabel, setSourceLabel] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { if (user === false) navigate("/admin/login"); }, [user, navigate]);

  useEffect(() => {
    if (!isNew && user) {
      api.get(`/admin/articles/${id}`).then((r) => setForm({ ...EMPTY, ...r.data }));
    }
  }, [id, isNew, user]);

  // set default category once categories load (for new articles)
  useEffect(() => {
    if (isNew && categories.length > 0 && !form.category_slug) {
      setForm((f) => ({ ...f, category_slug: categories[0].slug }));
    }
  }, [categories, isNew, form.category_slug]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) update("tags", [...form.tags, t]);
    setTagInput("");
  };
  const removeTag = (t) => update("tags", form.tags.filter((x) => x !== t));

  const addFact = () => { if (factInput.trim()) update("key_facts", [...form.key_facts, factInput.trim()]); setFactInput(""); };
  const removeFact = (i) => update("key_facts", form.key_facts.filter((_, x) => x !== i));

  const addSource = () => {
    if (sourceUrl.trim() && sourceLabel.trim()) {
      update("sources", [...form.sources, { label: sourceLabel.trim(), url: sourceUrl.trim() }]);
      setSourceLabel(""); setSourceUrl("");
    }
  };
  const removeSource = (i) => update("sources", form.sources.filter((_, x) => x !== i));

  // Gallery images
  const addGalleryImage = (url, caption = "") => update("gallery_images", [...form.gallery_images, { url, caption }]);
  const updateGalleryImage = (i, key, value) => {
    const next = [...form.gallery_images];
    next[i] = { ...next[i], [key]: value };
    update("gallery_images", next);
  };
  const removeGalleryImage = (i) => update("gallery_images", form.gallery_images.filter((_, x) => x !== i));

  // QA pairs
  const addQA = () => update("qa_pairs", [...form.qa_pairs, { question: "", answer: "" }]);
  const updateQA = (i, key, value) => {
    const next = [...form.qa_pairs];
    next[i] = { ...next[i], [key]: value };
    update("qa_pairs", next);
  };
  const removeQA = (i) => update("qa_pairs", form.qa_pairs.filter((_, x) => x !== i));

  // Media blocks
  const addMediaBlock = (type) => {
    const next = [...form.media_blocks, { type, url: "", caption: "", text: "", position: 1 }];
    update("media_blocks", next);
  };
  const updateMediaBlock = (i, key, value) => {
    const next = [...form.media_blocks];
    next[i] = { ...next[i], [key]: value };
    update("media_blocks", next);
  };
  const removeMediaBlock = (i) => update("media_blocks", form.media_blocks.filter((_, x) => x !== i));

  // Generic file uploader (used for featured image + gallery + inline image media block)
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url; // relative path like /api/files/...
  };

  const handleFeaturedUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { update("featured_image", await uploadFile(file)); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || "Upload échoué"); }
    finally { setUploading(false); }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    setUploading(true);
    try {
      for (const f of files) {
        const url = await uploadFile(f);
        addGalleryImage(url);
      }
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleMediaBlockUpload = async (i, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { updateMediaBlock(i, "url", await uploadFile(file)); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async (status = form.status) => {
    setError(""); setSaving(true);
    const payload = { ...form, status };
    try {
      let res;
      if (isNew) {
        res = await api.post("/admin/articles", payload);
        navigate(`/admin/articles/${res.data.id}`, { replace: true });
      } else {
        res = await api.put(`/admin/articles/${id}`, payload);
        setForm({ ...EMPTY, ...res.data });
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Sauvegarde impossible");
    } finally { setSaving(false); }
  };

  if (!user || user === null) return <div className="py-32 text-center font-mono-label">CHARGEMENT…</div>;

  const wordCount = (form.body || "").split(/\s+/).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#FAF8F3]" data-testid="admin-editor">
      <header className="border-b-2 border-[#1A1A18] sticky top-0 bg-[#FAF8F3] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="font-mono-label flex items-center gap-1 hover:text-[#4A7A3D]" data-testid="editor-back"><ArrowLeft size={14} /> Retour</Link>
            <Logo size="sm" />
            <span className="font-mono-label text-[#4B4B48] hidden md:inline">{TEMPLATE_LABELS[form.template]}</span>
          </div>
          <div className="flex gap-2">
            {form.status === "published" && form.slug && (
              <Link to={`/article/${form.slug}`} target="_blank" className="border-2 border-[#1A1A18] px-4 py-2 font-mono-label hover:bg-[#1A1A18] hover:text-[#FAF8F3] flex items-center gap-2" data-testid="editor-preview">
                <Eye size={14} /> Aperçu
              </Link>
            )}
            <button onClick={() => save("draft")} disabled={saving} className="border-2 border-[#1A1A18] px-4 py-2 font-mono-label hover:bg-[#F5EFEB] disabled:opacity-60" data-testid="editor-save-draft">
              <Save size={14} className="inline mr-2" /> Brouillon
            </button>
            <button onClick={() => save("published")} disabled={saving} className="bg-[#4A7A3D] text-[#FAF8F3] px-4 py-2 font-mono-label hover:bg-[#3E6633] disabled:opacity-60" data-testid="editor-publish">
              {saving ? "…" : "Publier"}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10">
        <div>
          <div className="font-mono-label text-[#4A7A3D] mb-2">{isNew ? "Nouvel article" : `Édition · ${form.title || "Sans titre"}`}</div>
          <h1 className="font-display text-3xl md:text-5xl leading-[1.05]">Éditeur</h1>
        </div>

        {error && <div className="border-2 border-red-700 text-red-700 p-4 font-mono-label" data-testid="editor-error">{error}</div>}

        {/* Template */}
        <section>
          <label className="font-mono-label text-[#4B4B48] block mb-3">Template (11 disponibles)</label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {TEMPLATES.map((t) => (
              <button key={t.v} type="button" onClick={() => update("template", t.v)} className={`text-left p-3 border-2 ${form.template === t.v ? "border-[#4A7A3D] bg-[#F5EFEB]" : "border-[#E0D8CE] hover:border-[#1A1A18]"}`} data-testid={`editor-tpl-${t.v}`}>
                <div className="font-display text-base">{t.l}</div>
                <div className="text-xs text-[#4B4B48] mt-1">{t.d}</div>
              </button>
            ))}
          </div>
        </section>

        {/* Title */}
        <section>
          <label className="font-mono-label text-[#4B4B48] block mb-2">Titre</label>
          <input type="text" maxLength={120} value={form.title} onChange={(e) => update("title", e.target.value)}
            className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 font-display text-2xl md:text-3xl outline-none focus:border-[#4A7A3D]" data-testid="editor-title" />
          <div className="font-mono-label text-[#4B4B48] mt-1">{form.title.length}/120</div>
        </section>

        <section>
          <label className="font-mono-label text-[#4B4B48] block mb-2">Sous-titre</label>
          <textarea rows={2} maxLength={200} value={form.subtitle} onChange={(e) => update("subtitle", e.target.value)} className="bg-transparent border-2 border-[#E0D8CE] w-full p-3 font-quote italic text-lg outline-none focus:border-[#4A7A3D]" data-testid="editor-subtitle" />
        </section>

        {/* Featured image */}
        <section>
          <label className="font-mono-label text-[#4B4B48] block mb-2">Image principale {form.template === "infographic" && "(infographie XL)"}</label>
          {form.featured_image && <img src={form.featured_image} alt="" className="w-full max-h-72 object-cover mb-3 border border-[#E0D8CE]" />}
          <div className="flex flex-wrap gap-3 items-center">
            <label className="border-2 border-[#1A1A18] px-4 py-2 font-mono-label cursor-pointer hover:bg-[#1A1A18] hover:text-[#FAF8F3] flex items-center gap-2">
              <Upload size={14} /> {uploading ? "Upload…" : "Uploader"}
              <input type="file" accept="image/*" className="hidden" onChange={handleFeaturedUpload} data-testid="editor-image-upload" />
            </label>
            <input type="url" placeholder="ou URL d'image…" value={form.featured_image} onChange={(e) => update("featured_image", e.target.value)} className="bg-transparent border-b-2 border-[#1A1A18] flex-1 py-2 outline-none focus:border-[#4A7A3D]" data-testid="editor-image-url" />
          </div>
        </section>

        {/* Body */}
        <section>
          <label className="font-mono-label text-[#4B4B48] block mb-2">Corps de l'article</label>
          <textarea rows={12} value={form.body} onChange={(e) => update("body", e.target.value)} className="bg-transparent border-2 border-[#E0D8CE] w-full p-4 font-body text-base leading-[1.7] outline-none focus:border-[#4A7A3D]" placeholder="Sépare les paragraphes par une ligne vide. Utilise **gras** pour mettre en évidence." data-testid="editor-body" />
          <div className="font-mono-label text-[#4B4B48] mt-1">{wordCount} mots · ≈ {Math.max(1, Math.round(wordCount / 200))} min de lecture</div>
        </section>

        {/* TEMPLATE-SPECIFIC SECTIONS */}
        {form.template === "myth_buster" && (
          <section className="grid md:grid-cols-2 gap-4 border-2 border-[#1A1A18] p-4">
            <div>
              <label className="font-mono-label text-[#C4944A] block mb-2">Mythe</label>
              <textarea rows={4} value={form.myth} onChange={(e) => update("myth", e.target.value)} className="bg-transparent border-b-2 border-[#1A1A18] w-full p-2 font-quote italic outline-none" data-testid="editor-myth" />
            </div>
            <div>
              <label className="font-mono-label text-[#4A7A3D] block mb-2">Réalité</label>
              <textarea rows={4} value={form.reality} onChange={(e) => update("reality", e.target.value)} className="bg-transparent border-b-2 border-[#1A1A18] w-full p-2 outline-none" data-testid="editor-reality" />
            </div>
          </section>
        )}

        {form.template === "data" && (
          <section className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="font-mono-label text-[#4B4B48] block mb-2">Valeur statistique</label>
              <input value={form.stat_value} onChange={(e) => update("stat_value", e.target.value)} className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 font-display text-2xl outline-none" data-testid="editor-stat-value" />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono-label text-[#4B4B48] block mb-2">Légende</label>
              <input value={form.stat_label} onChange={(e) => update("stat_label", e.target.value)} className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 outline-none" data-testid="editor-stat-label" />
            </div>
          </section>
        )}

        {form.template === "case_study" && (
          <section>
            <label className="font-mono-label text-[#4B4B48] block mb-2">Pays / Région</label>
            <input value={form.country} onChange={(e) => update("country", e.target.value)} className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 outline-none" data-testid="editor-country" />
          </section>
        )}

        {form.template === "video" && (
          <section className="space-y-3">
            <div>
              <label className="font-mono-label text-[#4B4B48] block mb-2">URL vidéo (YouTube ou Vimeo)</label>
              <input type="url" value={form.video_url} onChange={(e) => update("video_url", e.target.value)} placeholder="https://www.youtube.com/watch?v=..." className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 outline-none" data-testid="editor-video-url" />
            </div>
            <div>
              <label className="font-mono-label text-[#4B4B48] block mb-2">Légende</label>
              <input value={form.video_caption} onChange={(e) => update("video_caption", e.target.value)} className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 outline-none" data-testid="editor-video-caption" />
            </div>
          </section>
        )}

        {form.template === "podcast" && (
          <section className="space-y-3">
            <div>
              <label className="font-mono-label text-[#4B4B48] block mb-2">URL audio (SoundCloud, Spotify ou .mp3)</label>
              <input type="url" value={form.audio_url} onChange={(e) => update("audio_url", e.target.value)} placeholder="https://soundcloud.com/..." className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 outline-none" data-testid="editor-audio-url" />
            </div>
            <div>
              <label className="font-mono-label text-[#4B4B48] block mb-2">Légende</label>
              <input value={form.audio_caption} onChange={(e) => update("audio_caption", e.target.value)} className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 outline-none" data-testid="editor-audio-caption" />
            </div>
            <div>
              <label className="font-mono-label text-[#4B4B48] block mb-2">Code embed personnalisé (optionnel)</label>
              <textarea rows={3} value={form.embed_html} onChange={(e) => update("embed_html", e.target.value)} placeholder='<iframe src="..."></iframe>' className="bg-transparent border-2 border-[#E0D8CE] w-full p-2 font-mono text-xs outline-none focus:border-[#4A7A3D]" data-testid="editor-embed-html" />
            </div>
          </section>
        )}

        {form.template === "gallery" && (
          <section className="border-2 border-[#1A1A18] p-4">
            <div className="flex items-center justify-between mb-4">
              <label className="font-mono-label text-[#4A7A3D]">Galerie ({form.gallery_images.length}/10)</label>
              <label className="border-2 border-[#1A1A18] px-3 py-1.5 font-mono-label cursor-pointer hover:bg-[#1A1A18] hover:text-[#FAF8F3] flex items-center gap-2">
                <Upload size={14} /> Ajouter des images
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleGalleryUpload} data-testid="editor-gallery-upload" />
              </label>
            </div>
            {form.gallery_images.length === 0 ? (
              <p className="font-mono-label text-[#4B4B48] py-6 text-center">Aucune image. Uploade jusqu'à 10 images pour ta galerie.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {form.gallery_images.map((img, i) => (
                  <div key={i} className="border border-[#E0D8CE] p-2" data-testid={`editor-gallery-item-${i}`}>
                    <img src={img.url} alt="" className="w-full aspect-square object-cover" />
                    <input value={img.caption} onChange={(e) => updateGalleryImage(i, "caption", e.target.value)} placeholder="Légende" className="bg-transparent border-b border-[#1A1A18] w-full text-xs py-1 mt-2 outline-none" />
                    <button onClick={() => removeGalleryImage(i)} className="text-red-700 font-mono-label text-xs mt-2 hover:underline">Retirer</button>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {form.template === "qa" && (
          <section className="border-2 border-[#1A1A18] p-4 space-y-4">
            <div className="flex items-center justify-between">
              <label className="font-mono-label text-[#4A7A3D]">Questions / Réponses ({form.qa_pairs.length})</label>
              <button onClick={addQA} className="border-2 border-[#1A1A18] px-3 py-1.5 font-mono-label hover:bg-[#1A1A18] hover:text-[#FAF8F3] flex items-center gap-2" data-testid="editor-qa-add"><Plus size={14} /> Q/R</button>
            </div>
            {form.qa_pairs.map((qa, i) => (
              <div key={i} className="border border-[#E0D8CE] p-3 space-y-2 bg-[#F5EFEB]" data-testid={`editor-qa-${i}`}>
                <div className="flex items-start justify-between">
                  <span className="font-mono-label text-[#4A7A3D]">Q{i + 1}</span>
                  <button onClick={() => removeQA(i)} className="text-red-700"><X size={14} /></button>
                </div>
                <textarea rows={2} value={qa.question} onChange={(e) => updateQA(i, "question", e.target.value)} placeholder="Question…" className="bg-transparent border-b-2 border-[#1A1A18] w-full p-1 font-display text-lg outline-none" />
                <textarea rows={3} value={qa.answer} onChange={(e) => updateQA(i, "answer", e.target.value)} placeholder="Réponse…" className="bg-transparent border-2 border-[#E0D8CE] w-full p-2 outline-none focus:border-[#4A7A3D]" />
              </div>
            ))}
          </section>
        )}

        {/* Inline media blocks (available for ALL templates) */}
        <section className="border-2 border-[#E0D8CE] p-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div>
              <div className="font-mono-label text-[#4A7A3D]">Blocs médias inline ({form.media_blocks.length})</div>
              <div className="text-xs text-[#4B4B48] mt-1">Insère un média entre tes paragraphes (position = index du paragraphe après lequel l'insérer).</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => addMediaBlock("image")} className="border border-[#1A1A18] px-3 py-1.5 font-mono-label flex items-center gap-1 text-xs hover:bg-[#1A1A18] hover:text-[#FAF8F3]" data-testid="editor-media-add-image"><ImageIcon size={12} /> Image</button>
              <button onClick={() => addMediaBlock("video")} className="border border-[#1A1A18] px-3 py-1.5 font-mono-label flex items-center gap-1 text-xs hover:bg-[#1A1A18] hover:text-[#FAF8F3]" data-testid="editor-media-add-video"><Video size={12} /> Vidéo</button>
              <button onClick={() => addMediaBlock("audio")} className="border border-[#1A1A18] px-3 py-1.5 font-mono-label flex items-center gap-1 text-xs hover:bg-[#1A1A18] hover:text-[#FAF8F3]" data-testid="editor-media-add-audio"><Music size={12} /> Audio</button>
              <button onClick={() => addMediaBlock("quote")} className="border border-[#1A1A18] px-3 py-1.5 font-mono-label flex items-center gap-1 text-xs hover:bg-[#1A1A18] hover:text-[#FAF8F3]" data-testid="editor-media-add-quote"><Quote size={12} /> Citation</button>
            </div>
          </div>
          {form.media_blocks.length === 0 ? (
            <p className="font-mono-label text-[#4B4B48] text-xs py-2">Aucun bloc inline.</p>
          ) : (
            <div className="space-y-3">
              {form.media_blocks.map((b, i) => (
                <div key={i} className="border border-[#E0D8CE] p-3 bg-[#F5EFEB] space-y-2" data-testid={`editor-media-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono-label text-[#4A7A3D] uppercase">{b.type}</span>
                    <div className="flex items-center gap-2">
                      <label className="font-mono-label text-[#4B4B48] text-xs">Position après ¶</label>
                      <input type="number" min="0" value={b.position} onChange={(e) => updateMediaBlock(i, "position", parseInt(e.target.value) || 0)} className="bg-transparent border-b border-[#1A1A18] w-16 py-1 outline-none text-sm" data-testid={`editor-media-position-${i}`} />
                      <button onClick={() => removeMediaBlock(i)} className="text-red-700"><X size={14} /></button>
                    </div>
                  </div>
                  {b.type === "quote" ? (
                    <>
                      <textarea rows={2} value={b.text} onChange={(e) => updateMediaBlock(i, "text", e.target.value)} placeholder="Citation…" className="bg-transparent border-2 border-[#E0D8CE] w-full p-2 outline-none focus:border-[#4A7A3D] font-quote italic" />
                      <input value={b.caption} onChange={(e) => updateMediaBlock(i, "caption", e.target.value)} placeholder="Auteur (optionnel)" className="bg-transparent border-b border-[#1A1A18] w-full py-1 outline-none text-sm" />
                    </>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-2 items-center">
                        {b.type === "image" && (
                          <label className="border border-[#1A1A18] px-2 py-1 font-mono-label cursor-pointer text-xs hover:bg-[#1A1A18] hover:text-[#FAF8F3]">
                            <Upload size={12} className="inline mr-1" /> Upload
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleMediaBlockUpload(i, e)} />
                          </label>
                        )}
                        <input type="url" value={b.url} onChange={(e) => updateMediaBlock(i, "url", e.target.value)} placeholder={b.type === "video" ? "URL YouTube/Vimeo" : b.type === "audio" ? "URL SoundCloud/Spotify/MP3" : "URL ou upload"} className="bg-transparent border-b border-[#1A1A18] flex-1 min-w-[200px] py-1 outline-none text-sm" />
                      </div>
                      {b.type === "image" && b.url && <img src={b.url} alt="" className="max-h-32 mt-2" />}
                      <input value={b.caption} onChange={(e) => updateMediaBlock(i, "caption", e.target.value)} placeholder="Légende (optionnel)" className="bg-transparent border-b border-[#1A1A18] w-full py-1 outline-none text-sm" />
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Meta */}
        <section className="grid md:grid-cols-3 gap-6">
          <div>
            <label className="font-mono-label text-[#4B4B48] block mb-2">Rubrique</label>
            <select value={form.category_slug} onChange={(e) => update("category_slug", e.target.value)} className="bg-transparent border-2 border-[#1A1A18] w-full p-2 font-mono-label" data-testid="editor-category">
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono-label text-[#4B4B48] block mb-2">Langue</label>
            <select value={form.language} onChange={(e) => update("language", e.target.value)} className="bg-transparent border-2 border-[#1A1A18] w-full p-2 font-mono-label" data-testid="editor-language">
              {Object.entries(LANG_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-3 font-mono-label cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={(e) => update("featured", e.target.checked)} className="w-5 h-5" data-testid="editor-featured" />
              Article à la une
            </label>
          </div>
        </section>

        {/* Tags */}
        <section>
          <label className="font-mono-label text-[#4B4B48] block mb-2">Mots-clés</label>
          <div className="flex flex-wrap gap-2 mb-3">
            {form.tags.map((t) => (
              <span key={t} className="font-mono-label bg-[#F5EFEB] border border-[#E0D8CE] px-3 py-1 flex items-center gap-2">
                #{t} <button onClick={() => removeTag(t)}><X size={12} /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} placeholder="Ajouter un mot-clé" className="bg-transparent border-b-2 border-[#1A1A18] flex-1 py-2 outline-none" data-testid="editor-tag-input" />
            <button onClick={addTag} className="border-2 border-[#1A1A18] px-4 py-2 font-mono-label hover:bg-[#1A1A18] hover:text-[#FAF8F3]" data-testid="editor-tag-add">+</button>
          </div>
        </section>

        {/* Key facts */}
        <section>
          <label className="font-mono-label text-[#4B4B48] block mb-2">À retenir (encadré)</label>
          <ul className="space-y-2 mb-3">
            {form.key_facts.map((f, i) => (
              <li key={i} className="flex justify-between items-start gap-3 border-l-4 border-[#4A7A3D] pl-3 py-1 bg-[#F5EFEB]">
                <span>{f}</span><button onClick={() => removeFact(i)}><X size={14} /></button>
              </li>
            ))}
          </ul>
          <div className="flex gap-2">
            <input value={factInput} onChange={(e) => setFactInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFact(); } }} placeholder="Ajouter un fait clé" className="bg-transparent border-b-2 border-[#1A1A18] flex-1 py-2 outline-none" data-testid="editor-fact-input" />
            <button onClick={addFact} className="border-2 border-[#1A1A18] px-4 py-2 font-mono-label hover:bg-[#1A1A18] hover:text-[#FAF8F3]" data-testid="editor-fact-add">+</button>
          </div>
        </section>

        {/* Sources */}
        <section>
          <label className="font-mono-label text-[#4B4B48] block mb-2">Sources</label>
          <ul className="space-y-2 mb-3">
            {form.sources.map((s, i) => (
              <li key={i} className="flex justify-between items-center gap-3 border border-[#E0D8CE] p-3">
                <div>
                  <div className="font-mono-label text-[#1A1A18]">{s.label}</div>
                  <div className="text-xs text-[#4B4B48] truncate">{s.url}</div>
                </div>
                <button onClick={() => removeSource(i)}><X size={14} /></button>
              </li>
            ))}
          </ul>
          <div className="grid md:grid-cols-2 gap-2">
            <input value={sourceLabel} onChange={(e) => setSourceLabel(e.target.value)} placeholder="Libellé" className="bg-transparent border-b-2 border-[#1A1A18] py-2 outline-none" data-testid="editor-source-label" />
            <input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="URL" className="bg-transparent border-b-2 border-[#1A1A18] py-2 outline-none" data-testid="editor-source-url" />
          </div>
          <button onClick={addSource} className="mt-2 border-2 border-[#1A1A18] px-4 py-2 font-mono-label hover:bg-[#1A1A18] hover:text-[#FAF8F3]" data-testid="editor-source-add">+ Ajouter source</button>
        </section>
      </main>
    </div>
  );
}
